import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Ship, Anchor, Clock, Calendar, FileText, CheckCircle2, MessageCircle } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { fmtNum, waLink, waRateMessage } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

function Info({ label, value }) {
  return (
    <div className="border-b border-border py-2.5 flex justify-between items-center">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-mono-data font-medium text-navy">{value || "-"}</span>
    </div>
  );
}

export default function RateDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const goBook = () => {
    if (!user) { navigate("/login"); return; }
    navigate(`/booking?rate_id=${r.id}`);
  };

  useEffect(() => {
    api.get(`/rates/${id}`).then(({ data }) => setR(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <Layout><div className="max-w-3xl mx-auto px-6 py-24 text-center"><p className="font-heading text-2xl text-navy">Rate not found</p><Link to="/rates?shipment_type=export"><Button className="mt-4 bg-signal text-white">Back to rates</Button></Link></div></Layout>;
  if (!r) return <Layout><div className="h-1 bg-signal/30" /><div className="max-w-6xl mx-auto px-6 py-24 text-center text-muted-foreground">Loading rate…</div></Layout>;

  const localTax = r.local_charges.reduce((s, c) => s + c.amount * (1 + (c.tax_percent || 0) / 100), 0);

  return (
    <Layout>
      <div className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-4 transition-colors" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to results
          </button>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-signal text-white border-0 rounded-sm capitalize">{r.shipment_type}</Badge>
                <Badge variant="outline" className="border-white/30 text-white rounded-sm">{r.routing}</Badge>
              </div>
              <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">{r.pol_name} → {r.pod_name}</h1>
              <p className="text-white/60 mt-1 font-mono-data">{r.shipping_line_name} · {r.size_label} · {r.container_type}</p>
            </div>
            <Button className="bg-signal hover:bg-signal/90 text-white rounded-sm" onClick={() => navigate(`/enquiry?rate_id=${r.id}`)} data-testid="plan-shipment-btn">
              Plan This Shipment
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <section className="border border-border rounded-md p-6 bg-card">
            <h2 className="font-heading font-bold text-lg text-navy flex items-center gap-2 mb-4"><Anchor className="h-5 w-5 text-signal" /> Shipment</h2>
            <div className="grid sm:grid-cols-2 gap-x-8">
              <Info label="POL" value={r.pol_name} />
              <Info label="POD" value={r.pod_name} />
              <Info label="Shipping Line" value={r.shipping_line_name} />
              <Info label="Service" value={r.service} />
              <Info label="Transit Time" value={`${r.transit_time} days`} />
              <Info label="Frequency" value={r.frequency} />
              <Info label="Vessel Name" value={r.vessel_name} />
              <Info label="Transshipment Port" value={r.via_port} />
              <Info label="ETD (Departure)" value={(r.etd || "").slice(0, 10)} />
              <Info label="ETA (Arrival)" value={(r.eta || "").slice(0, 10)} />
              <Info label="Routing" value={r.routing} />
            </div>
          </section>

          <section className="border border-border rounded-md p-6 bg-card">
            <h2 className="font-heading font-bold text-lg text-navy flex items-center gap-2 mb-4"><Ship className="h-5 w-5 text-signal" /> Ocean Freight</h2>
            <div className="grid sm:grid-cols-2 gap-x-8">
              <Info label="Base Ocean Freight" value={`${r.currency} ${fmtNum(r.ocean_freight)}`} />
              <Info label="Freight Basis" value={r.freight_basis} />
              <Info label="Container Type" value={r.container_type} />
              <Info label="Container Size" value={r.size_label} />
              <Info label="Free Time" value={r.free_time} />
              <Info label="Cargo Type" value={r.cargo_type} />
              <Info label="Valid From" value={(r.valid_from || "").slice(0, 10)} />
              <Info label="Valid Until" value={(r.valid_until || "").slice(0, 10)} />
            </div>
            {(r.surcharges || []).length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase text-muted-foreground mb-2">Freight Surcharges</div>
                <div className="border border-border rounded-sm overflow-hidden">
                  {r.surcharges.map((s, i) => (
                    <div key={`${s.name}-${i}`} className="flex justify-between px-4 py-2.5 border-b border-border last:border-0 text-sm" data-testid={`detail-surcharge-${i}`}>
                      <span className="text-navy">{s.name}</span>
                      <span className="font-mono-data text-navy">{s.currency || r.currency} {fmtNum(s.amount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between px-4 py-3 bg-secondary/60 text-sm font-semibold">
                    <span className="text-navy">Ocean Freight + Surcharges</span>
                    <span className="font-mono-data text-navy">{r.currency} {fmtNum(Number(r.ocean_freight) + Number(r.surcharge_total || 0))}</span>
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className="border border-border rounded-md p-6 bg-card">
            <h2 className="font-heading font-bold text-lg text-navy flex items-center gap-2 mb-4"><FileText className="h-5 w-5 text-signal" /> Local Charges</h2>
            {r.local_charges.length === 0 ? (
              <p className="text-sm text-muted-foreground">No local charges configured for this line/route.</p>
            ) : (
              <div className="border border-border rounded-sm overflow-hidden">
                {r.local_charges.map((c) => (
                  <div key={c.id} className="flex justify-between px-4 py-2.5 border-b border-border last:border-0 text-sm">
                    <span className="text-navy">{c.charge_type} {c.tax_percent ? <span className="text-xs text-muted-foreground">(+{c.tax_percent}% GST)</span> : null}</span>
                    <span className="font-mono-data text-navy">{c.currency} {fmtNum(c.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between px-4 py-3 bg-secondary/60 text-sm font-semibold">
                  <span className="text-navy">Total Local Charges (incl. tax)</span>
                  <span className="font-mono-data text-navy">INR {fmtNum(localTax)}</span>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Sticky cost summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-20 border border-border rounded-md bg-card overflow-hidden">
            <div className="bg-navy text-white p-5">
              <div className="text-xs uppercase text-white/50 tracking-wide">Estimated Shipment Cost</div>
              <div className="text-xs text-white/50 mt-1">for 1 × {r.size_label}</div>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Ocean Freight</span>
                <span className="font-mono-data font-semibold text-navy">{r.currency} {fmtNum(r.ocean_freight)}</span>
              </div>
              {Number(r.surcharge_total || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Freight Surcharges</span>
                  <span className="font-mono-data font-semibold text-navy">{r.currency} {fmtNum(r.surcharge_total)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Local Charges</span>
                <span className="font-mono-data font-semibold text-navy">INR {fmtNum(r.local_charges_inr)}</span>
              </div>
              <div className="border-t border-border pt-3 flex justify-between">
                <span className="font-semibold text-navy">Estimated Total</span>
                <span className="font-mono-data font-bold text-signal">{r.currency} {fmtNum(Number(r.ocean_freight) + Number(r.surcharge_total || 0))} + INR {fmtNum(r.local_charges_inr)}</span>
              </div>
              <Button className="w-full bg-signal hover:bg-signal/90 text-white rounded-sm mt-2" onClick={goBook} data-testid="book-rate-btn">
                Book This Rate
              </Button>
              <Button variant="outline" className="w-full rounded-sm" onClick={() => window.open(`${API}/rates/${r.id}/quote.pdf`, "_blank")} data-testid="download-pdf-btn">
                Download PDF Quote
              </Button>
              <Button variant="outline" className="w-full rounded-sm" onClick={() => navigate(`/calculator?rate_id=${r.id}`)} data-testid="open-calculator-btn">
                Open in Calculator
              </Button>
              <Button variant="outline" className="w-full rounded-sm" onClick={() => navigate(`/enquiry?rate_id=${r.id}`)} data-testid="get-quote-btn">
                Get Final Quote
              </Button>
              <a href={waLink(waRateMessage(r))} target="_blank" rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center h-9 rounded-sm bg-[#25D366] text-white text-sm font-medium hover:bg-[#1da851] transition-colors"
                data-testid="whatsapp-enquiry-btn">
                <MessageCircle className="h-4 w-4 mr-2" /> Enquire on WhatsApp
              </a>
              <p className="text-[11px] text-muted-foreground leading-relaxed pt-2 flex gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-signal" />
                Rates are indicative and subject to final confirmation at the time of booking.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
